import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";

import "../../styles/Form.css";
import logo from "../../images/logo.png";

const CUSTOM_FIELD_TOKEN_REGEX =
  /{{\s*(text_field|date_field):([a-zA-Z0-9_-]+)\s*}}/g;

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const getCustomFieldTokens = (html = "") => {
  const fields = [];
  const seen = new Set();
  let match;

  CUSTOM_FIELD_TOKEN_REGEX.lastIndex = 0;
  while ((match = CUSTOM_FIELD_TOKEN_REGEX.exec(html))) {
    const id = match[2];
    if (seen.has(id)) continue;

    seen.add(id);
    fields.push({
      id,
      type: match[1] === "date_field" ? "date" : "text",
    });
  }

  return fields;
};

const CustomTaskPage = () => {
  const { t_key, userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const loggedInUserId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const effectiveUserId = isAdmin && paramUserId ? paramUserId : loggedInUserId;
  const isSlidePage = location.pathname.startsWith("/slide/");

  const [task, setTask] = useState(null);
  const [user, setUser] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [taskStatusValue, setTaskStatusValue] = useState("not_completed");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSavingFiles, setIsSavingFiles] = useState(false);
  const [isDeletingAttachmentId, setIsDeletingAttachmentId] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const taskContentRef = useRef(null);
  const customFieldValuesRef = useRef({});
  const customFieldSaveTimerRef = useRef(null);

  const refreshTaskStatus = useCallback(() => {
    if (!task?.t_id || !effectiveUserId) return Promise.resolve();

    return axios
      .get(`${API_BASE}/api/usertasks/status/${effectiveUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const idKey = String(task.t_id);
        const keyKey = String(task.t_key || "");
        const status =
          res.data?.status?.[idKey] ??
          res.data?.status?.[keyKey] ??
          "not_completed";
        setTaskStatusValue(status);
      })
      .catch((err) => {
        console.error("Failed to load custom task status", err);
      });
  }, [API_BASE, effectiveUserId, task, token]);

  // --------------------------------------------------
  // Load Custom Task
  // --------------------------------------------------
  useEffect(() => {
    axios
      .get(`${API_BASE}/api/custom-tasks/by-key/${t_key}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setTask(res.data))
      .catch(() => {
        alert("Task not found");
        navigate("/userhome");
      });
  }, [API_BASE, t_key, token, navigate]);

  // --------------------------------------------------
  // Load User (Employee Name)
  // --------------------------------------------------
  useEffect(() => {
    if (!effectiveUserId) return;

    axios
      .get(`${API_BASE}/api/users/${effectiveUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUser(res.data))
      .catch(() => console.error("Failed to load user"));
  }, [API_BASE, effectiveUserId, token]);

  useEffect(() => {
    if (!task?.t_allow_upload || !effectiveUserId) return;

    axios
      .get(
        `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
          task.t_key,
        )}/${effectiveUserId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((res) => {
        setUploadedFiles(Array.isArray(res.data) ? res.data : []);
        refreshTaskStatus();
      })
      .catch((err) => {
        console.error("Failed to load custom task attachments", err);
      });
  }, [API_BASE, task, effectiveUserId, token, refreshTaskStatus]);

  useEffect(() => {
    refreshTaskStatus();
  }, [refreshTaskStatus]);

  useEffect(() => {
    return () => {
      if (customFieldSaveTimerRef.current) {
        clearTimeout(customFieldSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!task?.t_key || !effectiveUserId) return;

    axios
      .get(
        `${API_BASE}/api/custom-tasks/fields/${encodeURIComponent(
          task.t_key,
        )}/${effectiveUserId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        const fields = res.data?.fields || {};
        customFieldValuesRef.current = fields;
        setCustomFieldValues(fields);
        setExpiryDate(fields.expiryDate || "");
        setIssueDate(fields.issueDate || "");
        setCardNumber(fields.cardNumber || "");
      })
      .catch((err) => {
        console.error("Failed to load custom task fields", err);
      });
  }, [API_BASE, effectiveUserId, task, token]);

  const saveTaskDates = async (override = {}) => {
    if (
      !task?.t_show_expiry &&
      !task?.t_show_issue_date &&
      !task?.t_show_card_number
    ) {
      return;
    }

    const normalizeDate = (value) => (value ? value : null);
    const normalizeText = (value) => {
      if (value === null || value === undefined) return null;
      const trimmed = String(value).trim();
      return trimmed || null;
    };
    const resolvedExpiryDate = normalizeDate(override.expiryDate ?? expiryDate);
    const resolvedIssueDate = normalizeDate(override.issueDate ?? issueDate);
    const resolvedCardNumber = normalizeText(
      override.cardNumber ?? cardNumber,
    );

    await axios.post(
      `${API_BASE}/api/custom-tasks/expiry`,
      {
        taskKey: task.t_key,
        userId: effectiveUserId,
        expiryDate: resolvedExpiryDate,
        issueDate: resolvedIssueDate,
        cardNumber: resolvedCardNumber,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  };

  const isReviewed = taskStatusValue === "completed";
  const canEditAttachments = task?.t_allow_upload === 1 && !isReviewed;

  const handleCancel = () => {
    if (isSlidePage) {
      navigate(-1);
      return;
    }
    if (isAdmin && paramUserId) {
      navigate(`/userhome/${paramUserId}`);
    } else {
      navigate("/userhome");
    }
  };

  if (!task || !user) return null;

  const employeeName = `${user.first_name} ${user.last_name}`;
  const today = new Date().toLocaleDateString();
  const customFields = getCustomFieldTokens(task.t_content);
  const hasCustomFields = customFields.length > 0;

  const injectTaskVariables = (html) => {
    if (!html) return "";

    return html
      .replace(CUSTOM_FIELD_TOKEN_REGEX, (match, fieldType, fieldId) => {
        const type = fieldType === "date_field" ? "date" : "text";
        const value = customFieldValues[fieldId] ?? "";
        const inputClass =
          type === "date"
            ? "custom-task-inline-field custom-task-date-field"
            : "custom-task-inline-field";
        const disabled = isReviewed ? " disabled" : "";

        return `<input type="${type}" class="${inputClass}" data-custom-task-field="${escapeHtml(
          fieldId,
        )}" data-custom-task-field-type="${type}" value="${escapeHtml(
          value,
        )}"${disabled} />`;
      })
      .replaceAll("{{employee_name}}", escapeHtml(employeeName))
      .replaceAll("{{task_title}}", escapeHtml(task.t_display_title))
      .replaceAll("{{date}}", escapeHtml(today));
  };

  const collectCustomFieldValues = ({ requireValues = false } = {}) => {
    const nextValues = { ...customFieldValuesRef.current };
    const missingFields = [];
    const inputs =
      taskContentRef.current?.querySelectorAll("[data-custom-task-field]") ||
      [];

    inputs.forEach((input) => {
      const fieldId = input.getAttribute("data-custom-task-field");
      if (!fieldId) return;

      const value = input.value || "";
      nextValues[fieldId] = value;

      if (requireValues && !value.trim()) {
        missingFields.push(fieldId);
      }
    });

    if (missingFields.length) {
      throw new Error("Please complete all inserted fields before continuing.");
    }

    return nextValues;
  };

  const saveCustomFieldValues = async ({
    requireValues = false,
    updateState = true,
  } = {}) => {
    if (!hasCustomFields) return;

    const fields = collectCustomFieldValues({ requireValues });

    await axios.post(
      `${API_BASE}/api/custom-tasks/fields`,
      {
        taskKey: task.t_key,
        userId: effectiveUserId,
        fields,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    customFieldValuesRef.current = fields;
    if (updateState) {
      setCustomFieldValues(fields);
    }
  };

  const handleCustomFieldInput = (e) => {
    const fieldId = e.target?.dataset?.customTaskField;
    if (!fieldId) return;

    customFieldValuesRef.current = {
      ...customFieldValuesRef.current,
      [fieldId]: e.target.value || "",
    };

    if (customFieldSaveTimerRef.current) {
      clearTimeout(customFieldSaveTimerRef.current);
    }

    customFieldSaveTimerRef.current = setTimeout(() => {
      saveCustomFieldValues({ updateState: false }).catch((err) => {
        console.error("Failed to auto-save custom fields", err);
      });
    }, 700);
  };

  const handleCustomFieldBlur = () => {
    if (customFieldSaveTimerRef.current) {
      clearTimeout(customFieldSaveTimerRef.current);
      customFieldSaveTimerRef.current = null;
    }

    saveCustomFieldValues().catch((err) => {
      console.error("Failed to auto-save custom fields", err);
    });
  };

  const handleSelectFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    setPendingFiles((prev) => {
      const existingKeys = new Set(
        prev.map((file) => `${file.name}_${file.size}_${file.lastModified}`),
      );
      const next = [...prev];

      for (const file of selected) {
        const key = `${file.name}_${file.size}_${file.lastModified}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push(file);
        }
      }

      return next;
    });

    e.target.value = "";
  };

  const removePendingFile = (fileToRemove) => {
    setPendingFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          ),
      ),
    );
  };

  const handleSaveAttachments = async (e) => {
    e.preventDefault();

    try {
      setIsSavingFiles(true);

      if (pendingFiles.length) {
        const formData = new FormData();
        pendingFiles.forEach((file) => {
          formData.append("files", file);
        });
        formData.append("userId", effectiveUserId);
        formData.append("taskKey", task.t_key);
        formData.append("categoryId", task.t_category_id);

        await axios.post(
          `${API_BASE}/api/custom-tasks/upload-attachment?taskKey=${encodeURIComponent(
            task.t_key,
          )}&userId=${effectiveUserId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      }

      await saveTaskDates();
      await saveCustomFieldValues({ requireValues: true });

      if (task.t_show_signature !== 1) {
        await axios.post(
          `${API_BASE}/api/usertasks/submit`,
          {
            userId: effectiveUserId,
            taskId: task.t_id,
            taskName: task.t_key,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        await refreshTaskStatus();
      }

      if (pendingFiles.length) {
        const refreshed = await axios.get(
          `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
            task.t_key,
          )}/${effectiveUserId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (Array.isArray(refreshed.data)) {
          setUploadedFiles(refreshed.data);
        }

        setPendingFiles([]);
      }

      await refreshTaskStatus();

      alert("Saved successfully");
    } catch (err) {
      console.error("Upload failed", err);
      const message =
        err.response?.data?.message || "Failed to save";
      alert(message);
    } finally {
      setIsSavingFiles(false);
    }
  };

  const handleDeleteSavedAttachment = async (attachment) => {
    const confirmed = window.confirm(
      `Delete uploaded file "${attachment.ta_original_name}"?`,
    );
    if (!confirmed) return;

    try {
      setIsDeletingAttachmentId(attachment.ta_id);
      await axios.delete(`${API_BASE}/api/custom-tasks/attachments/${attachment.ta_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploadedFiles((prev) =>
        prev.filter((file) => file.ta_id !== attachment.ta_id),
      );
      await refreshTaskStatus();
    } catch (err) {
      console.error("Delete failed", err);
      const message =
        err.response?.data?.message || "Failed to delete attachment";
      alert(message);
    } finally {
      setIsDeletingAttachmentId(null);
    }
  };

  return (
    <>
      {/* 🔝 TOP BAR */}
      <div className="d-flex justify-content-between align-items-center pt-3 px-3">
        {!isSlidePage && (
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            {task.t_display_title}
          </span>
        )}

        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            {isSlidePage ? "Close" : "Return"}
          </button>
        </div>
      </div>

      {/* 📄 TASK BODY */}
      <div
        className={`form-wrapper my-4 print-area ${isSlidePage ? "slide-narrow" : ""}`}
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        {isSlidePage && (
          <div
            className="d-flex justify-content-between align-items-center px-3"
            style={{
              paddingTop: "16px",
              paddingBottom: "12px",
              marginBottom: "16px",
              borderBottom: "1px solid #ccc",
            }}
          >
            <h4
              className="m-0"
              style={{ fontSize: 18, fontWeight: 500, color: "#333" }}
            >
              Review and Sign Consent – {task.t_display_title}
            </h4>
          </div>
        )}

        {/* HEADER */}
        {!isSlidePage && (
          <div className="row align-items-center mb-4">
            <div className="col-3">
              <img src={logo} alt="Logo" style={{ width: "150px" }} />
            </div>

            <div className="col-6 text-center">
              <h2 className="m-0">{task.t_display_title}</h2>
            </div>

            <CompanyAddress />
          </div>
        )}

        {/* TASK CONTENT */}
        {task.t_pdf ? (
          <iframe
            src={`${API_BASE}${task.t_pdf}`}
            width="100%"
            height="800px"
            style={{ border: "none" }}
            title="Task PDF"
          />
        ) : (
          <div
            ref={taskContentRef}
            onInput={handleCustomFieldInput}
            onChange={handleCustomFieldInput}
            onBlur={handleCustomFieldBlur}
            dangerouslySetInnerHTML={{
              __html: injectTaskVariables(task.t_content),
            }}
          />
        )}

        {/* EXPIRY DATE (TOGGLED) */}
        {(task.t_show_card_number === 1 ||
          task.t_show_expiry === 1 ||
          task.t_show_issue_date === 1) && (
          <div className="row mb-4">
            {task.t_show_card_number === 1 && (
              <div className="col-md-4">
                <label className="form-label">Card Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={cardNumber}
                  disabled={isReviewed}
                  onChange={(e) => setCardNumber(e.target.value)}
                  onBlur={(e) =>
                    saveTaskDates({ cardNumber: e.target.value })
                  }
                />
              </div>
            )}

            {task.t_show_issue_date === 1 && (
              <div className="col-md-4">
                <label className="form-label">Issue Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={issueDate}
                  disabled={isReviewed}
                  onChange={(e) => setIssueDate(e.target.value)}
                  onBlur={(e) =>
                    saveTaskDates({ issueDate: e.target.value })
                  }
                />
              </div>
            )}

            {task.t_show_expiry === 1 && (
              <div className="col-md-4">
                <label className="form-label">Expiry Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={expiryDate}
                  disabled={isReviewed}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  onBlur={(e) =>
                    saveTaskDates({ expiryDate: e.target.value })
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* EMPLOYEE NAME + DATE */}
        {(task.t_show_employee === 1 || task.t_show_date === 1) && (
          <div className="row mb-4">
            {task.t_show_employee === 1 && (
              <div className="col-md-6">
                <label className="form-label">Employee Name</label>
                <input className="form-control" value={employeeName} readOnly />
              </div>
            )}

            {task.t_show_date === 1 && (
              <div className="col-md-6">
                <label className="form-label">Date</label>
                <input className="form-control" value={today} readOnly />
              </div>
            )}
          </div>
        )}

        {/* ==================================================
            Upload Section (ONLY if enabled)
           ================================================== */}
        {task.t_allow_upload === 1 && (
          <>
            <hr className="my-4" />

            <h5>Attachments</h5>

            <form onSubmit={handleSaveAttachments}>
              <div className="mb-3">
                <label>Select File (PDF / JPG / PNG)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  disabled={!canEditAttachments || isSavingFiles}
                  onChange={handleSelectFiles}
                />
              </div>

              {pendingFiles.length > 0 && (
                <div className="mt-4 p-3 bg-light rounded border">
                  <div className="text-muted mb-2">Files Ready To Save</div>
                  <ul className="list-unstyled mb-0">
                    {pendingFiles.map((pendingFile, idx) => (
                      <li
                        key={`${pendingFile.name}_${pendingFile.size}_${pendingFile.lastModified}_${idx}`}
                        className="d-flex justify-content-between align-items-center py-1"
                      >
                        <span>{pendingFile.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removePendingFile(pendingFile)}
                          disabled={!canEditAttachments || isSavingFiles}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </form>

            {uploadedFiles.length > 0 && (
              <div className="mt-4 p-3 bg-light rounded border">
                <div className="text-muted mb-2">Uploaded Files</div>

                <ul className="list-unstyled mb-0">
                  {uploadedFiles.map((f) => (
                    <li
                      key={f.ta_id}
                      className="d-flex justify-content-between align-items-center py-1"
                    >
                      <a
                        href={`${API_BASE}/${f.ta_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={f.ta_original_name}
                      >
                        {f.ta_original_name}
                      </a>
                      {canEditAttachments && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteSavedAttachment(f)}
                          disabled={isDeletingAttachmentId === f.ta_id}
                          title="Delete"
                        >
                          {isDeletingAttachmentId === f.ta_id ? (
                            "Deleting..."
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isReviewed && (
              <div className="alert alert-info mt-3 mb-0">
                Attachments and task dates cannot be edited after review.
              </div>
            )}

            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveAttachments}
                disabled={!canEditAttachments || isSavingFiles}
              >
                {isSavingFiles ? "Saving..." : "Save"}
              </button>
            </div>
          </>
        )}

        <hr className="my-5" />

        {/* SIGNATURE + REVIEW (UNCHANGED) */}
        {task.t_show_signature === 1 && (
          <SignatureManager
            taskId={task.t_id}
            taskName={task.t_key}
            userId={effectiveUserId}
            isAdmin={isAdmin}
            onBeforeSign={async () => {
              await saveTaskDates();
              await saveCustomFieldValues({ requireValues: true });
            }}
          />
        )}

        <ReviewManager
          taskId={task.t_id}
          taskName={task.t_key}
          userId={effectiveUserId}
          isAdmin={isAdmin}
          onReviewed={refreshTaskStatus}
        />
      </div>
    </>
  );
};

export default CustomTaskPage;
